import React, { useState } from "react";
import authService from "../../services/authService";
import Modal from "./Modal";

const GenerateInviteModal = ({ onClose, onSuccess }) => {
  const [role, setRole] = useState("BERUFSBILDNER");
  const [profession, setProfession] = useState("");
  const [expiresIn, setExpiresIn] = useState(7);
  const [isGlobal, setIsGlobal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const supportsPersistentLink = ["BERUFSBILDNER", "PRACTICE_INSTRUCTOR", "REVISOR"].includes(role);
  const supportsProfessionType = role !== "REVISOR";

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setInviteUrl("");

    try {
      if (isGlobal) {
        if (!supportsPersistentLink) {
             setError("Global links are only supported for Berufsbildner, Practice Instructor and Revisor.");
             setLoading(false);
             return;
        }
        const result = await authService.regenerateGlobalInvitation(role);
        if (result.success) {
            setInviteUrl(result.url);
            if (onSuccess) onSuccess(); // Notify parent component immediately
        } else {
            setError(result.message || "Failed to regenerate global link.");
        }
      } else {
          const payload = {
            role,
            professionType: supportsProfessionType ? profession || null : null,
            expiresInDays: parseInt(expiresIn, 10),
          };

          const result = await authService.createInvitation(payload);

          if (result.success) {
            setInviteUrl(result.invitationUrl); // Fixed property access based on API
            if (onSuccess) onSuccess(); // Notify parent component immediately
          } else {
            setError(result.message || "Failed to generate invitation link.");
          }
      }
    } catch (err) {
      console.error("Invitation generation failed", err);
      setError("Failed to generate invitation link.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Clipboard copy failed", err);
      setError("Could not copy the link to the clipboard.");
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Generate invitation link"
      subtitle="Create a secure onboarding link for a new member"
      icon="fa-envelope-open-text"
      size="lg"
    >
      <div>
          {error && (
            <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
              <i className="fas fa-exclamation-triangle mt-0.5"></i>
              <div className="text-sm">{error}</div>
            </div>
          )}

          {!inviteUrl ? (
            <>
              <div className="space-y-5">
                {/* Role */}
                <div>
                  <label className="block text-sm font-semibold text-grey-dusk mb-2">
                    Role
                  </label>
                  <select
                        className="input-brand w-full"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        disabled={loading}
                    >
                        <option value="BERUFSBILDNER">Berufsbildner</option>
                        <option value="PRACTICE_INSTRUCTOR">Practice Instructor</option>
                      <option value="REVISOR">Revisor</option>
                    </select>

                  {/* Multi-use Toggle */}
                  {supportsPersistentLink && (
                      <div className="mt-4 flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <input
                              type="checkbox"
                              id="isGlobalLink"
                              checked={isGlobal}
                              onChange={(e) => setIsGlobal(e.target.checked)}
                              disabled={loading}
                              className="w-5 h-5 text-sunrise-red rounded focus:ring-sunrise-red border-gray-300"
                          />
                          <label htmlFor="isGlobalLink" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                              Generate Persistent (Multi-use) Link
                          </label>
                      </div>
                  )}
                  {isGlobal && (
                      <div className="mt-2 text-xs text-amber-600 flex items-start gap-2 bg-amber-50 p-2 rounded">
                          <i className="fas fa-exclamation-triangle mt-0.5"></i>
                          <span>
                              Warning: Generating a new persistent link will INVALIDATE any existing persistent link for this role immediately.
                          </span>
                      </div>
                  )}
                </div>

                {/* Profession */}
                {!isGlobal && supportsProfessionType && (
                <>
                <div>
                  <label className="block text-sm font-semibold text-grey-dusk mb-2">
                    Profession <span className="text-gray-400 font-medium">(optional)</span>
                  </label>
                  <select
                    className="input-brand w-full"
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Select profession...</option>
                    <option value="INFORMATIK">Informatik</option>
                    <option value="MEDIAMATIK">Mediamatik</option>
                    <option value="KV">KV</option>
                    <option value="KUNDENDIALOG">Kundendialog</option>
                    <option value="DETAILHANDEL">Detailhandel</option>
                  </select>

                  <div className="mt-2 text-xs text-gray-500 flex items-start gap-2">
                    <i className="fas fa-circle-info text-sunrise-red mt-0.5"></i>
                    <span>
                      If a profession is selected, the invited user cannot change it during signup.
                    </span>
                  </div>
                </div>

                {/* Expiration */}
                <div>
                  <label className="block text-sm font-semibold text-grey-dusk mb-2">
                    Valid for
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="1"
                      max="30"
                      className="input-brand w-28"
                      value={expiresIn}
                      onChange={(e) => setExpiresIn(e.target.value)}
                      disabled={loading}
                    />
                    <span className="text-sm text-gray-600">days</span>
                  </div>
                </div>
                </>
                )}

                {/* Info card */}
                <div className={`border rounded-xl p-4 ${isGlobal ? 'bg-amber-50 border-amber-200' : 'bg-ice-white border-pale-dawn'}`}>
                  <div className={`text-sm font-semibold flex items-center mb-2 ${isGlobal ? 'text-amber-800' : 'text-grey-dusk'}`}>
                    <i className={`fas ${isGlobal ? 'fa-link' : 'fa-shield-alt'} mr-2 ${isGlobal ? 'text-amber-600' : 'text-sunrise-red'}`}></i>
                    {isGlobal ? 'Persistent Link Details' : 'Invitation settings'}
                  </div>
                  <div className={`space-y-2 text-sm ${isGlobal ? 'text-amber-700' : 'text-gray-600'}`}>
                    <div className="flex items-start gap-2">
                       <i className={`fas fa-check mt-0.5 text-xs ${isGlobal ? 'text-amber-600' : 'text-sunrise-red'}`}></i>
                      <span>{isGlobal ? 'Link does not expire automatically' : 'Secure invitation URL for new onboarding'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                       <i className={`fas fa-check mt-0.5 text-xs ${isGlobal ? 'text-amber-600' : 'text-sunrise-red'}`}></i>
                      <span>{isGlobal ? 'Can be used for multiple registrations' : 'Automatically expires after the selected time window'}</span>
                    </div>
                    <div className="flex items-start gap-2">
                       <i className={`fas fa-check mt-0.5 text-xs ${isGlobal ? 'text-amber-600' : 'text-sunrise-red'}`}></i>
                      <span>{isGlobal ? 'Regenerating invalidates the previous link' : 'Use this for internal roles only'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row gap-3 justify-end mt-6 pt-5 border-t border-pale-dawn">
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
                  disabled={loading}
                >
                  <i className="fas fa-times mr-2"></i>
                  Cancel
                </button>

                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="inline-flex items-center justify-center px-5 py-3 rounded-lg bg-sunrise-red text-white font-semibold hover:bg-sunrise-600 transition-colors"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin mr-2"></i>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-link mr-2"></i>
                      Generate Link
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Success state */}
              <div className="text-center">
                <div className="w-16 h-16 bg-green-50 border border-green-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-check text-green-600 text-2xl"></i>
                </div>

                <h4 className="text-lg font-bold text-grey-dusk mb-2">
                  Invitation link created
                </h4>

                <p className="text-sm text-gray-600 mb-5">
                  Share this link securely with the invited person.
                </p>

                <div className="bg-ice-white border border-pale-dawn rounded-xl p-4 text-left mb-4">
                  <label className="block text-xs font-semibold text-gray-500 mb-2">
                    Invitation URL
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={inviteUrl}
                    onClick={(e) => e.target.select()}
                    className="w-full rounded-lg border border-pale-dawn bg-white px-3 py-2 text-sm font-mono text-grey-dusk"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={copyToClipboard}
                    className={`w-full rounded-xl px-4 py-3 font-semibold text-sm transition-colors flex items-center justify-center ${
                      copied
                        ? "bg-green-600 text-white"
                        : "bg-sunrise-red text-white hover:bg-sunrise-600"
                    }`}
                  >
                    {copied ? (
                      <>
                        <i className="fas fa-check mr-2"></i>
                        Copied
                      </>
                    ) : (
                      <>
                        <i className="fas fa-copy mr-2"></i>
                        Copy Link
                      </>
                    )}
                  </button>

                  <button
                    onClick={onClose}
                    className="flex-1 px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center"
                  >
                    <i className="fas fa-xmark mr-2"></i>
                    Close
                  </button>
                </div>

                <div className="mt-4 text-xs text-gray-500 flex items-center justify-center gap-2">
                  <i className="fas fa-clock text-sunrise-red"></i>
                  {isGlobal
                    ? "Persistent link"
                    : `Valid for ${expiresIn} day${Number(expiresIn) === 1 ? "" : "s"}`}
                </div>
              </div>
            </>
          )}
      </div>
    </Modal>
  );
};

export default GenerateInviteModal;
